import zod from 'zod';
import { MoniIntegrationFormSchema } from '../../../services/IntegrationService/MoniService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateMoniIntegrationAsync,
  useGetMoniIntegrationByIdAsync,
  useUpdateMoniIntegrationAsync,
} from '../../../services/IntegrationService/MoniService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { t } from 'i18next';

interface MoniEditionFormProps {
  moniIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type MoniEditionFormInput = zod.infer<typeof MoniIntegrationFormSchema>;

const MoniEditionForm = ({
  moniIdToUpdate,
  handleSaveForm,
}: MoniEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } = useForm<MoniEditionFormInput>({
    resolver: zodResolver(MoniIntegrationFormSchema),
    defaultValues: {
      name: '',
      httpPort: '',
      password: '',
      username: '',
      serverIp: '',
      description: '',
      isActive: true,
      occurrenceSet: '1',
      company: '1',
    },
  });

  const {
    mutate: mutateCreateMoniIntegration,
    isLoading: isLoadingCreateMoniIntegration,
  } = useCreateMoniIntegrationAsync();
  const {
    mutate: mutateUpdateMoniIntegration,
    isLoading: isLoadingUpdateMoniIntegration,
  } = useUpdateMoniIntegrationAsync();
  const {
    mutateAsync: mutateGetMoniIntegrationByIdAsync,
    isLoading: isLoadingGetMoniIntegrationById,
  } = useGetMoniIntegrationByIdAsync();

  const handleSave = (data: MoniEditionFormInput) => {
    if (moniIdToUpdate) {
      mutateUpdateMoniIntegration(
        { id: moniIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração moni atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateMoniIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração moni criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (moniIdToUpdate) {
      mutateGetMoniIntegrationByIdAsync(moniIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [moniIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.name')}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.ip')}
              />
            )}
            name={'serverIp'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.door')}
              />
            )}
            name={'httpPort'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.user')}
              />
            )}
            name={'username'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.password')}
              />
            )}
            name={'password'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.jointOccurrenceCode')}
              />
            )}
            name={'occurrenceSet'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.enterprise')}
              />
            )}
            name={'company'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.moniForms.description')}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.moniForms.active')}
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateMoniIntegration ||
              isLoadingGetMoniIntegrationById ||
              isLoadingUpdateMoniIntegration
            }
          >
            {t('integrationsPage.moniForms.save')}
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default MoniEditionForm;
